import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { mockRSSItems, rssFeedSources } from '@/data/newsData';
import { useRssFeeds } from '@/hooks/useRssFeeds';
import { cn } from '@/lib/utils';

const formatDate = (dateString) => {
  if (!dateString) {
    return 'Recently updated';
  }

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed)) {
    return 'Recently updated';
  }

  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1527335988388-b40ee248d80c?w=800&q=80&auto=format&fit=crop';

const mapItems = (items = []) =>
  items.map((item) => ({
    id: item.id,
    title: item.title,
    excerpt: item.excerpt,
    date: item.date,
    category: item.source,
    image: item.image || DEFAULT_IMAGE,
    href: item.link,
    isExternal: true,
  }));

export const NewsSection = ({ limit = 3, className }) => {
  const { items: rssItems, loading, error } = useRssFeeds(rssFeedSources, { limit });

  const fallbackItems = mapItems(
    (limit ? mockRSSItems.slice(0, limit) : mockRSSItems).map((item) => ({
      ...item,
      link: item.url,
      image: DEFAULT_IMAGE,
    }))
  );

  const feedItems = mapItems(rssItems);
  const displayNews = feedItems.length ? feedItems : fallbackItems;

  const renderCard = (item) => {
    const Wrapper = item.isExternal ? 'a' : Link;
    const wrapperProps = item.isExternal
      ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
      : { to: item.href };

    return (
      <Wrapper key={item.id} {...wrapperProps}>
        <Card className="group overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all duration-300 bg-white h-full">
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <Badge className="absolute top-4 left-4 bg-red-600 hover:bg-red-700">
              {item.category || 'Industry'}
            </Badge>
            {item.isExternal && (
              <span className="absolute top-4 right-4 bg-white/90 text-red-600 rounded-full p-2 shadow-sm">
                <ExternalLink className="w-4 h-4" />
              </span>
            )}
          </div>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(item.date)}</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors">
              {item.title}
            </h3>
            <p className="text-gray-600 text-sm line-clamp-2">
              {item.excerpt || 'Read the latest update from trusted industry sources.'}
            </p>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <span className="inline-flex items-center gap-2 text-red-600 font-medium text-sm group-hover:gap-3 transition-all">
                {item.isExternal ? 'View Source' : 'Read More'}
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </CardContent>
        </Card>
      </Wrapper>
    );
  };

  return (
    <section className={cn('py-20 bg-gray-50', className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Latest Updates</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">News & Insights</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-xl">
              Stay informed with the latest developments from trusted civil engineering and infrastructure newsrooms.
            </p>
            {loading && (
              <p className="text-sm text-gray-500 mt-2">Loading live industry headlines...</p>
            )}
            {error && (
              <p className="text-sm text-red-600 mt-2">
                Unable to load live feeds right now. Showing recent highlights instead.
              </p>
            )}
          </div>
          <Link
            to="/news"
            className="mt-6 md:mt-0 inline-flex items-center gap-2 text-red-600 font-semibold hover:gap-4 transition-all duration-300"
          >
            View All News
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayNews.map((item) => renderCard(item))}
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
